import type { QualityLevel } from "../core/types.ts";
import { PERFORMANCE } from "../core/config.ts";
import { setArenaLoadMotionProvider } from "./MotionSettings.ts";

/**
 * PerfGovernor (BL-44) — the ONE load brain.
 *
 * Lives in `engine/` (like MotionSettings) and reads RENDER-SIDE signals only;
 * it NEVER writes sim state, so it cannot move the golden master. It fuses two
 * load inputs — (1) reactive: the measured per-frame ms (the safety net) and
 * (2) proactive: live entity/particle counts (the wave-curve, BL-44 Part C) —
 * into a single discrete load level with hysteresis + RECOVERY (asymmetric
 * thresholds + cooldowns → no oscillation; fixes the old down-only bug), and
 * drives ONE drop-priority ladder — ARENA ONLY (cheapest first, motion +
 * bloom LAST):
 *
 *   1 hit-stop suppressed → 2 particle adaptive cap → 3 density recede →
 *   4 arena chroma off → 5 arena bloom step → 6 arena motion damp
 *
 * Recovery reverses the order. The HUD `UIBloom`/`UIChromeCanvas` + the skill
 * UI are NEVER touched (owner: HUD stays full fidelity).
 *
 * Actuators are injected (the arena `WebGL2Effects.setQuality` + the
 * `ParticleSystem` soft cap) so the governor stays decoupled + headless-testable.
 * Motion + density bias + hit-stop suppression are PULLED by their consumers.
 */

/** The render-side actuators the governor pushes to (never sim). */
export interface PerfActuators {
  /** Arena post-FX quality (chroma/bloom rungs). */
  setPostFxQuality(quality: QualityLevel): void;
  getPostFxQuality(): QualityLevel;
  /** Arena live-particle soft cap (rung 2). */
  setParticleCap(cap: number): void;
}

/** Mirrors ParticleSystem MAX_PARTICLES — the full (rung-0) ceiling. */
const PARTICLE_MAX = 500;

/**
 * Ladder lookup tables, indexed by the reactive load level 0..GOVERNOR_MAX_LEVEL.
 * Each column is one actuator's target at that level. Monotonic by construction.
 */
/** rung 2 — particle ceiling. Full at L0/L1; recedes through the deep levels. */
const PARTICLE_CAP_BY_LEVEL = [500, 500, 360, 280, 220, 170, 120] as const;
/** rung 3 — extra "virtual crowding" added to the live count so DensityGlow
 *  halos recede sooner. 0 until L3 (a deep, measured-gated cut). */
const DENSITY_BIAS_BY_LEVEL = [0, 0, 0, 24, 40, 56, 72] as const;
/** rungs 4-5 — arena post-FX quality. high (full) → medium (chroma off) →
 *  low (bloom off). The governor never sets "off" (that is the
 *  WebGL-unavailable state, which it must not stomp). */
const POSTFX_BY_LEVEL: readonly QualityLevel[] = [
  "high",
  "high",
  "high",
  "high",
  "medium",
  "low",
  "low",
];
/** rung 6 — the deepest the MEASURED budget alone may damp arena motion. The
 *  wave-curve (Part C) floors gentler than this; only real stutter reaches it. */
const MOTION_HARD_FLOOR = 0.6;

/**
 * BL-44 Part C — the cautious wave-progressive curve. ONE tunable config (not
 * scattered magic numbers). It pre-positions ONLY the cheap/least-noticed levers
 * (particle over-emission + arena motion magnitude), each held above a FLOOR, so
 * a strong machine at wave 35 still looks essentially full. It NEVER reaches the
 * deep visible cuts (density recede / chroma off / bloom step / hit-stop
 * suppression) — those stay gated behind the reactive MEASURED budget. That gate
 * is the quality floor the owner asked for ("nicht dass es am Ende schlecht aussieht").
 */
const WAVE_CURVE = {
  /** ≤ this wave: full fidelity, zero visible loss. */
  FULL_FIDELITY_WAVE: 15,
  /** waveBias reaches 1 at/after this wave (endless clamps here). */
  RAMP_END_WAVE: 35,
  /** The wave-curve never damps arena motion below this (a gentle ease). */
  MOTION_WAVE_FLOOR: 0.85,
  /** The wave-curve never caps particles below this fraction of the pool. */
  PARTICLE_WAVE_FLOOR: 0.7,
} as const;

function smoothstep(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** 0 below FULL_FIDELITY_WAVE, smoothstepping to 1 by RAMP_END_WAVE (and staying
 *  1 beyond, for endless). Exported pure so the wave-curve is unit-tested. */
export function waveCurveBias(wave: number): number {
  const { FULL_FIDELITY_WAVE: a, RAMP_END_WAVE: b } = WAVE_CURVE;
  if (wave <= a) return 0;
  return smoothstep((wave - a) / (b - a));
}

/** Short human labels per rung for the dev overlay. */
const RUNG_LABELS = [
  "hitstop",
  "particles",
  "density",
  "chroma",
  "bloom",
  "motion",
] as const;

/**
 * Module singleton for the density-recede bias (rung 3), so the decoupled arena
 * consumer (EnemyRenderer) can pull it the same way it pulls `motionScale` —
 * without threading the governor through every renderer. Registered by the
 * active governor's constructor; neutral (0) by default and in headless tests.
 */
let activeDensityBias: () => number = () => 0;

/** Extra "virtual crowding" the governor adds to the live enemy count so the
 *  BL-53 halos recede sooner under measured load. 0 without a governor. */
export function arenaDensityBias(): number {
  return activeDensityBias();
}

/** Test-only: force the rung-3 density bias, so a guard can exercise the load
 *  path the SHIPPED game actually takes. `arenaMotionScale()` never reaches 0
 *  in production (motion is pinned to 1 and this governor clamps at
 *  MOTION_HARD_FLOOR), so a test that only drives the motion scale to 0 proves
 *  a property of itself. */
export function __setDensityBiasForTest(bias: number): void {
  activeDensityBias = () => bias;
}

/** Test-only: drop any registered governor's density-bias hook. */
export function __resetPerfGovernorModuleForTest(): void {
  activeDensityBias = () => 0;
}

export interface PerfGovernorDebug {
  level: number;
  rungs: string;
  waveBias: number;
  motion: number;
  particleCap: number;
  postFx: QualityLevel;
  enemies: number;
  particles: number;
}

export class PerfGovernor {
  private readonly actuators: PerfActuators;

  /** Reactive sliding window of frame-ms (ring buffer). */
  private frameTimes: number[] = [];
  private frameIndex = 0;
  /** Asymmetric cooldown (ms) gating BOTH directions after a step. */
  private cooldownMs = 0;
  /** Current load level 0..GOVERNOR_MAX_LEVEL. */
  private level_ = 0;

  /** Proactive reads (overlay + Part C). */
  private enemyCount_ = 0;
  private particleCount_ = 0;
  /** Part C — the wave-curve bias ∈ [0,1] (0 ≤ wave 15; smoothstep to wave 35). */
  private waveBias_ = 0;

  /** Last pushed actuator values (push only on change). */
  private lastCap = -1;

  /**
   * The frame budget the DEGRADE decision measures against, in milliseconds.
   *
   * Default is `PERFORMANCE.MAX_FRAME_TIME_MS` (16) — the value this governor
   * has always used, so a run that sets nothing behaves byte-identically.
   *
   * The `adaptive_throttling` module lowers it to 14 for the length of one run:
   * its player-facing promise is literally "Quality reduces at 14ms" in both
   * locales, and until 2026-08-16 that promise had no wiring at all (filed as
   * GA-20 / P1 in the July audit, deferred on scope grounds, re-found by the
   * 2026-08-16 gameplay-effect audit as the catalogue's one DEAD selection).
   *
   * Deliberately NOT tied to the recovery threshold: the module promises an
   * earlier DEGRADE, not a stricter recovery, and `RECOVER_FRAME_TIME_MS` (12)
   * must stay below the budget for the hysteresis to hold — 14 > 12 does.
   *
   * `reset()` restores the default, so the value cannot leak into the next run.
   */
  private frameBudgetMs_: number = PERFORMANCE.MAX_FRAME_TIME_MS;

  /** Set the run's degrade budget (ms). Called once at run start from the
   *  committed module selection; `reset()` puts it back to the default. */
  setFrameBudgetMs(ms: number): void {
    this.frameBudgetMs_ = ms;
  }

  /** The budget the degrade decision is currently measuring against. */
  get frameBudgetMs(): number {
    return this.frameBudgetMs_;
  }

  constructor(actuators: PerfActuators) {
    this.actuators = actuators;
    // Expose the continuous signals to the decoupled arena consumers.
    setArenaLoadMotionProvider(() => this.loadMotionFactor());
    activeDensityBias = () => this.densityCountBias();
    this.applyActuators();
  }

  get level(): number {
    return this.level_;
  }

  /**
   * One render frame's worth of signal. `frameMs` is the measured update+render
   * time (the `renderPipeline` feed); counts are live render-side reads; `wave`
   * drives the proactive wave-curve (Part C). Drives the reactive level (with
   * recovery) and (re)applies the ladder actuators.
   */
  sample(
    frameMs: number,
    enemyCount: number,
    particleCount: number,
    wave: number = 0,
  ): void {
    this.enemyCount_ = enemyCount;
    this.particleCount_ = particleCount;
    this.waveBias_ = waveCurveBias(wave);
    if (frameMs > 0 && frameMs <= 1000) this.pushFrame(frameMs);
    this.applyActuators();
  }

  // ── ladder getters (pulled by the arena consumers) ──────────────────────

  /** rung 1 — suppress the kill hit-stop entirely under measured load. */
  shouldSuppressHitStop(): boolean {
    return this.level_ >= 1;
  }

  /** rung 3 — extra crowd added to the live count so halos recede sooner. */
  densityCountBias(): number {
    return DENSITY_BIAS_BY_LEVEL[this.level_] ?? 0;
  }

  /** rung 6 — continuous arena-motion factor ∈ [floor, 1]. The wave-curve eases
   *  it gently (down to MOTION_WAVE_FLOOR); only the MEASURED budget at the max
   *  level reaches the deeper MOTION_HARD_FLOOR. The more aggressive of the two
   *  wins, clamped to the hard floor. */
  loadMotionFactor(): number {
    const reactive =
      this.level_ >= PERFORMANCE.GOVERNOR_MAX_LEVEL ? MOTION_HARD_FLOOR : 1;
    const wave = lerp(1, WAVE_CURVE.MOTION_WAVE_FLOOR, this.waveBias_);
    const v = Math.min(reactive, wave);
    return v < MOTION_HARD_FLOOR ? MOTION_HARD_FLOOR : v;
  }

  /** Reset to full fidelity (run restart). Restores every actuator. */
  reset(): void {
    this.level_ = 0;
    this.frameTimes.length = 0;
    this.frameIndex = 0;
    this.cooldownMs = 0;
    this.enemyCount_ = 0;
    this.particleCount_ = 0;
    this.waveBias_ = 0;
    this.lastCap = -1;
    // The run's degrade budget is per-run state like everything above it: a
    // run without `adaptive_throttling` must measure against 16 again, even if
    // the previous run set 14. `resetForNewRun` calls this before the new
    // selection is applied, so the module re-sets it if it is picked again.
    this.frameBudgetMs_ = PERFORMANCE.MAX_FRAME_TIME_MS;
    this.applyActuators();
  }

  debug(): PerfGovernorDebug {
    const rungs =
      this.level_ === 0
        ? "—"
        : RUNG_LABELS.slice(0, this.level_).join("+");
    return {
      level: this.level_,
      rungs,
      waveBias: Number(this.waveBias_.toFixed(2)),
      motion: Number(this.loadMotionFactor().toFixed(2)),
      particleCap: this.lastCap,
      postFx: this.actuators.getPostFxQuality(),
      enemies: this.enemyCount_,
      particles: this.particleCount_,
    };
  }

  // ── internals ───────────────────────────────────────────────────────────

  /** Effective particle ceiling: the more aggressive of the reactive rung and
   *  the wave-curve's gentle over-emission trim (floored at PARTICLE_WAVE_FLOOR
   *  of the pool — so wave alone never starves the field). */
  private effectiveParticleCap(): number {
    const reactive = PARTICLE_CAP_BY_LEVEL[this.level_] ?? PARTICLE_MAX;
    const wave = Math.round(
      PARTICLE_MAX * lerp(1, WAVE_CURVE.PARTICLE_WAVE_FLOOR, this.waveBias_),
    );
    return Math.min(reactive, wave);
  }

  private applyActuators(): void {
    const cap = this.effectiveParticleCap();
    if (cap !== this.lastCap) {
      this.actuators.setParticleCap(cap);
      this.lastCap = cap;
    }

    const target = POSTFX_BY_LEVEL[this.level_] ?? "high";
    const current = this.actuators.getPostFxQuality();
    // Never stomp "off" — that is the WebGL-unavailable state, not a governor
    // rung (the governor never targets "off"). Recovery re-raises from low/med.
    if (current !== "off" && target !== current) {
      this.actuators.setPostFxQuality(target);
    }
  }

  private pushFrame(ms: number): void {
    const window = PERFORMANCE.GOVERNOR_FRAME_WINDOW;
    if (this.frameTimes.length < window) this.frameTimes.push(ms);
    else this.frameTimes[this.frameIndex] = ms;
    this.frameIndex = (this.frameIndex + 1) % window;

    if (this.frameTimes.length < window) return; // need a full window

    if (this.cooldownMs > 0) {
      this.cooldownMs = Math.max(0, this.cooldownMs - ms);
      return;
    }

    let sum = 0;
    let over = 0;
    let under = 0;
    for (const t of this.frameTimes) {
      sum += t;
      if (t > this.frameBudgetMs_) over++;
      if (t < PERFORMANCE.RECOVER_FRAME_TIME_MS) under++;
    }
    const avg = sum / this.frameTimes.length;

    // UP (degrade) — sustained over budget. Fast cooldown.
    if (
      avg > this.frameBudgetMs_ &&
      over >= PERFORMANCE.AUTO_DEGRADE_THRESHOLD_FRAMES &&
      this.level_ < PERFORMANCE.GOVERNOR_MAX_LEVEL
    ) {
      this.level_++;
      this.cooldownMs = PERFORMANCE.DEGRADE_COOLDOWN_MS;
      this.resetWindow();
      return;
    }

    // DOWN (recover) — comfortably under budget for a clean window. Slow
    // cooldown + a stricter threshold (asymmetry) so it never oscillates.
    if (
      avg < PERFORMANCE.RECOVER_FRAME_TIME_MS &&
      under >= PERFORMANCE.RECOVER_THRESHOLD_FRAMES &&
      this.level_ > 0
    ) {
      this.level_--;
      this.cooldownMs = PERFORMANCE.RECOVER_COOLDOWN_MS;
      this.resetWindow();
    }
  }

  private resetWindow(): void {
    this.frameTimes.length = 0;
    this.frameIndex = 0;
  }
}
